// PSGC data fetching from local backend database
// Data is seeded via seed-psgc script and stored in psgc_cache table
// Served via /api/psgc/data - NO external API dependency

const API_BASE = '/api/psgc'; // Local backend endpoint only

// Ensure proper UTF-8 encoding for special characters like ñ
function normalizeText(text) {
  if (!text) return text;

  // Fix common UTF-8 encoding issues
  text = text
    .replace(/Ã±/g, 'ñ')
    .replace(/Ã¡/g, 'á')
    .replace(/Ã©/g, 'é')
    .replace(/Ã­/g, 'í')
    .replace(/Ã³/g, 'ó')
    .replace(/Ãº/g, 'ú')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã¿/g, 'ÿ')
    .replace(/Ã'/g, 'Ñ')
    .replace(/Ã€/g, 'À')
    .replace(/Ãˆ/g, 'È')
    .replace(/ÃŒ/g, 'Ì')
    .replace(/Ã'/g, 'Ò')
    .replace(/Ã™/g, 'Ù');

  // Normalize to NFC form to handle composed/decomposed Unicode
  return text.normalize('NFC');
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API fetch failed: ${res.status}`);
  return await res.json();
}

// Global cache for PSGC data
let psgcCache = null;
let cachePromise = null;

// Global cache for nationalities data
let nationalitiesCache = null;
let nationalitiesPromise = null;

// Global cache for nature of work data
let natureOfWorkCache = null;
let natureOfWorkPromise = null;

// Get all PSGC data with global caching
export async function getAllPSGCData() {
  // Return cached data immediately if available
  if (psgcCache) {
    return psgcCache;
  }

  // Return existing promise if already loading
  if (cachePromise) {
    return cachePromise;
  }

  // Start loading and cache the promise
  cachePromise = fetchJson(`${API_BASE}/data`).then(result => {
    if (result.success && result.data) {
      psgcCache = result.data;
      cachePromise = null; // Reset promise
      return psgcCache;
    }
    throw new Error('No PSGC data available');
  }).catch(error => {
    cachePromise = null; // Reset on error
    throw error;
  });

  return cachePromise;
}

// Prefetch nationalities data
export async function getNationalities() {
  // Return cached data immediately if available
  if (nationalitiesCache) {
    return nationalitiesCache;
  }

  // Return existing promise if already loading
  if (nationalitiesPromise) {
    return nationalitiesPromise;
  }

  // Start loading and cache the promise
  nationalitiesPromise = fetchJson('/api/nationalities').then(result => {
    nationalitiesCache = result;
    nationalitiesPromise = null; // Reset promise
    return nationalitiesCache;
  }).catch(error => {
    nationalitiesPromise = null; // Reset on error
    throw error;
  });

  return nationalitiesPromise;
}

// Prefetch nature of work data
export async function getNatureOfWork() {
  // Return cached data immediately if available
  if (natureOfWorkCache) {
    return natureOfWorkCache;
  }

  // Return existing promise if already loading
  if (natureOfWorkPromise) {
    return natureOfWorkPromise;
  }

  // Start loading and cache the promise
  natureOfWorkPromise = fetchJson('/api/natureofwork').then(result => {
    natureOfWorkCache = result;
    natureOfWorkPromise = null; // Reset promise
    return natureOfWorkCache;
  }).catch(error => {
    natureOfWorkPromise = null; // Reset on error
    throw error;
  });

  return natureOfWorkPromise;
}

// Initialize - prefetch PSGC, nationalities, and nature of work data
export async function initializePSGCDatabase() {
  try {
    console.log('🚀 Prefetching reference data...');

    // Start all requests in parallel
    const [psgcData, nationalitiesData, natureOfWorkData] = await Promise.all([
      getAllPSGCData().catch(error => {
        console.warn('⚠️ Failed to prefetch PSGC data:', error.message);
        return null;
      }),
      getNationalities().catch(error => {
        console.warn('⚠️ Failed to prefetch nationalities data:', error.message);
        return null;
      }),
      getNatureOfWork().catch(error => {
        console.warn('⚠️ Failed to prefetch nature of work data:', error.message);
        return null;
      })
    ]);

    if (psgcData) {
      console.log('✅ PSGC data prefetched');
    }
    if (nationalitiesData) {
      console.log('✅ Nationalities data prefetched');
    }
    if (natureOfWorkData) {
      console.log('✅ Nature of work data prefetched');
    }

    console.log('🎉 All reference data ready!');
  } catch (error) {
    console.error('❌ Failed to prefetch reference data:', error);
    // Don't throw - allow app to continue even if prefetch fails
  }
}

// Clear cache - now actually clears the global cache
export function clearCache() {
  psgcCache = null;
  cachePromise = null;
  nationalitiesCache = null;
  nationalitiesPromise = null;
  natureOfWorkCache = null;
  natureOfWorkPromise = null;
}

export async function getRegions() {
  try {
    const data = await getAllPSGCData();
    return data.regions.map(item => ({
      ...item,
      name: normalizeText(item.name)
    }));
  } catch (error) {
    console.error('Failed to fetch regions:', error);
    // Return empty array - component will handle gracefully
    return [];
  }
}

export async function getProvincesByRegion(regionCode) {
  try {
    const data = await getAllPSGCData();
    const provinces = data.provinces.filter(p => p.regionCode === regionCode);
    return provinces.map(item => ({
      ...item,
      name: normalizeText(item.name)
    }));
  } catch (error) {
    console.error('Failed to fetch provinces:', error);
    return [];
  }
}

export async function getCitiesByRegion(regionCode) {
  try {
    const data = await getAllPSGCData();
    const { cities } = data;

    // Special handling for NCR (National Capital Region)
    // NCR cities have provinceCode = null and regionCode = 1300000000
    const ncrCode = '1300000000';
    if (regionCode === ncrCode) {
      // For NCR, filter cities that have null provinceCode (indicating no province)
      let ncrCities = cities.filter(c => c.regionCode === ncrCode && c.provinceCode === null);

      // Special handling for Manila: exclude "City of Manila" since it has no direct barangays
      // Users should select specific districts like Tondo I/II, Binondo, etc.
      ncrCities = ncrCities.filter(c => c.code !== '1380600000'); // Exclude City of Manila

      return ncrCities.map(item => ({
        ...item,
        name: normalizeText(item.name)
      }));
    }

    // For non-NCR regions: filter cities by region code
    let filtered = cities.filter(c => c.regionCode === regionCode);

    // Exclude cities that are administrative groupings with no direct barangays
    // These cities have sub-divisions that contain the actual barangays
    const citiesToExclude = [
      '0103314000', // City of San Fernando (La Union) - has districts
      '0105532000', // City of San Carlos (Pangasinan) - has districts
      '0501724000', // City of Naga (Camarines Sur) - has surrounding municipalities
      '0604528000'  // City of Talisay (Negros Occidental) - has surrounding municipalities
    ];

    filtered = filtered.filter(c => !citiesToExclude.includes(c.code));

    return filtered.map(item => ({
      ...item,
      name: normalizeText(item.name)
    }));
  } catch (error) {
    console.error('Failed to fetch cities by region:', error);
    return [];
  }
}

export async function getCitiesByProvince(provinceCode) {
  try {
    const data = await getAllPSGCData();
    let cities = data.cities.filter(c => c.provinceCode === provinceCode);

    // Exclude cities that are administrative groupings with no direct barangays
    // These cities have sub-divisions that contain the actual barangays
    const citiesToExclude = [
      '0103314000', // City of San Fernando (La Union) - has districts
      '0105532000', // City of San Carlos (Pangasinan) - has districts
      '0501724000', // City of Naga (Camarines Sur) - has surrounding municipalities
      '0604528000'  // City of Talisay (Negros Occidental) - has surrounding municipalities
    ];

    cities = cities.filter(c => !citiesToExclude.includes(c.code));

    return cities.map(item => ({
      ...item,
      name: normalizeText(item.name)
    }));
  } catch (error) {
    console.error('Failed to fetch cities:', error);
    return [];
  }
}

export async function getBarangaysByCity(cityOrMunicipalityCode) {
  try {
    const data = await getAllPSGCData();

    const barangays = data.barangays.filter(b => b.cityCode === cityOrMunicipalityCode);

    return barangays.map(item => ({
      ...item,
      name: normalizeText(item.name)
    }));
  } catch (error) {
    console.error('Failed to fetch barangays:', error);
    return [];
  }
}

export function filterProvincesByRegion(provinces, regionCode) {
  return provinces.filter(p => p.regionCode === regionCode);
}

export function filterCitiesByProvince(cities, provinceCode) {
  return cities.filter(c => c.provinceCode === provinceCode);
}

export function filterBarangaysByCity(barangays, cityCode) {
  return barangays.filter(b => b.cityCode === cityCode);
}

